/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback Control tab component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.12.0
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectIsFeatureWithVersionEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';

import { Button, Tab, Tabs } from '../../../../components/common';
import ArrowTooltip from '../../../../components/common/Tooltip/Tooltip';
import { pushGTMAnalytic } from '../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { ICommonTranslation } from '../../../../modules/internationalization/types';
import { IPageCashbackTranslation } from '../../../../modules/internationalization/types/pages/cashback';
import { Loader } from '../../../../modules/loader/components/Loader';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { fonts } from '../../../../theme';
import { cashbackControlGetServiceListAction } from '../../actions/cashbackControlActions';
import { IPageCashbackReducer } from '../../pageCashbackReducer';
import {
    makeSelectCashbackServiceList,
    makeSelectCashbackServiceListIsError,
    makeSelectCashbackServiceListIsLoading,
    TCashbackServiceList,
} from '../../selectors/cashbackControlSelectors';
import { makeSelectCashbackMeta, makeSelectCashbackSelectCurrent } from '../../selectors/cashbackSelectors';
import {
    ECashbackService,
    ICashbackControlAvailableService,
    ICashbackControlConnectedService,
} from '../../types/cashbackControlTypes';
import {
    CashbackControlServiceListStyled,
    CashbackControlTabContainerStyled,
    CashbackNoServiceListStyled,
} from '../styled/CashbackControlStyled';
import { CashbackTextStyled } from '../styled/CashbackStyled';
import CashbackAutoSpendingBonuses from './CashbackAutoSpendingBonuses';
import CashbackControlServiceCard from './CashbackControlServiceCard';
import CashbackControlServiceError from './CashbackControlServiceError';
import CashbackControlServiceNoAvailable from './CashbackControlServiceNoAvailable';
import CashbackControlServiceNoConnected from './CashbackControlServiceNoConnected';
import CashbackReserve from './CashbackReserve/CashbackReserve';
import { CashbackServicesInfo } from './CashbackServicesInfo';


interface ICashbackControlServiceTabs {
    translationCashback: IPageCashbackTranslation;
    translationCommon: ICommonTranslation;
}

const CashbackControlServiceTabs = (
    props: ICashbackControlServiceTabs & IConnectedProps & IDispatchToProps,
) => {

    const {
        accountSelectCurrent,
        controlServiceListIsError,
        controlServiceListIsLoading,
        meta,
        serviceList,
        translationCashback,
        translationCommon,

        loadServiceList,
        pushGtmAnalytic,
    } = props;

    const tabControlTranslation = translationCashback.tabControl;
    const isCashbackV4Enabled = useSelector(makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CASHBACK, [4], true));

    // current tab state
    const [currentTabType, setCurrentTabType] = useState<ECashbackService>(ECashbackService.connected);
    const [currentAvailableService, setCurrentAvailableService] =
        useState<ICashbackControlAvailableService | null>(null);
    const [currentConnectedService, setCurrentConnectedService] =
        useState<ICashbackControlConnectedService | null>(null);

    useEffect(() => {
        loadServiceList();
    }, [loadServiceList, accountSelectCurrent.value]);

    const clickOnInactiveTab = useCallback((service: ECashbackService, label: string) => {
        if (currentTabType !== service) {
            pushGtmAnalytic({
                operation: GTMOperations.cashback.clickOnInactiveTabCashbackControlTab(label),
                withAccountType: true,
                withSusrId: true,
                withUserRole: true,
            });
        }
        setCurrentTabType(service);
    }, [currentTabType, pushGtmAnalytic]);

    const clickOnNoConnectedServicesLink = useCallback(() => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnLinksInCashbackPage(
                translationCashback.sideMenu.control,
                tabControlTranslation.statusNoConnectedServices.btn,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        setCurrentTabType(ECashbackService.available);
    }, [
        pushGtmAnalytic,
        tabControlTranslation.statusNoConnectedServices.btn,
        translationCashback.sideMenu.control,
    ]);

    const getActivationTooltip = useCallback((): string => {
        if (currentAvailableService && !currentAvailableService.activationLink) {
            return tabControlTranslation.btnTooltipNoActivationLink;
        } else if (!currentAvailableService) {
            return tabControlTranslation.btnTooltipNotChosen;
        }
        return '';
    } , [currentAvailableService, tabControlTranslation]);

    /*
    * -------------------------------------------
    *               Handlers
    * -------------------------------------------
    */

    const onLinkMoreClickHandler = useCallback((
        item: ICashbackControlAvailableService | ICashbackControlConnectedService,
        type: ECashbackService,
    ) => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnTileElementCashbackControlTab(
                (ECashbackService.connected === type)
                    ? tabControlTranslation.tabConnected
                    : tabControlTranslation.tabAvailable,
                item.title),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic, tabControlTranslation]);

    const onSelectServiceHandler = useCallback((
        item: ICashbackControlAvailableService | ICashbackControlConnectedService,
        type: ECashbackService,
    ) => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnTileCashbackControlTab(
                (ECashbackService.connected === type)
                    ? tabControlTranslation.tabConnected
                    : tabControlTranslation.tabAvailable,
                item.title),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        switch (type) {
            case ECashbackService.available:
                setCurrentAvailableService(
                    item.id === currentAvailableService?.id ? null : item as ICashbackControlAvailableService,
                );
                break;
            case ECashbackService.connected:
                setCurrentConnectedService(
                    item.id === currentConnectedService?.id ? null : item as ICashbackControlConnectedService,
                );
                break;
        }
    }, [currentAvailableService, currentConnectedService, pushGtmAnalytic, tabControlTranslation]);

    const isServiceListEmpty = useCallback(() => {
        return !controlServiceListIsError && !serviceList.controlServiceListConnected.length;
    }, [serviceList.controlServiceListConnected.length, controlServiceListIsError]);

    /*
    * -------------------------------------------
    *               Render
    * -------------------------------------------
    */

    const renderServiceConnectedCard = useCallback((item: ICashbackControlConnectedService) => (
        <CashbackControlServiceCard
            {...item}
            active={item.id === currentConnectedService?.id}
            cardTranslation={tabControlTranslation.serviceCard}
            key={item.id}
            scoreUnit={translationCommon.scores}
            onCardClick={() => onSelectServiceHandler(item, ECashbackService.connected)}
            onLinkMoreClick={() => onLinkMoreClickHandler(item, ECashbackService.connected)}
        />
    ), [
        currentConnectedService,
        onLinkMoreClickHandler,
        onSelectServiceHandler,
        tabControlTranslation,
        translationCommon,
    ]);

    const renderServiceAvailableCard = useCallback((item: ICashbackControlAvailableService) => (
        <CashbackControlServiceCard
            {...item}
            active={item.id === currentAvailableService?.id}
            cardTranslation={tabControlTranslation.serviceCard}
            key={item.id}
            onCardClick={() => onSelectServiceHandler(item, ECashbackService.available)}
            onLinkMoreClick={() => onLinkMoreClickHandler(item, ECashbackService.available)}
        />
    ), [
        currentAvailableService,
        onLinkMoreClickHandler,
        onSelectServiceHandler,
        tabControlTranslation,
    ]);

    return (
        <CashbackControlTabContainerStyled>
            <CashbackTextStyled font={fonts.textHeader3}>
                {isCashbackV4Enabled ? tabControlTranslation.newtitle : tabControlTranslation.title}
            </CashbackTextStyled>

            <Tabs active={currentTabType === ECashbackService.connected ? 0 : 1}>
                <Tab
                    className="cashback-control__tab tab-connected"
                    label={tabControlTranslation.tabConnected}
                    onClick={() => clickOnInactiveTab(ECashbackService.connected, tabControlTranslation.tabConnected)}
                >
                    <div>
                        <Loader showLoader={controlServiceListIsLoading}/>
                        {serviceList.controlServiceListConnected.length
                            ? <CashbackControlServiceListStyled>
                                {serviceList.controlServiceListConnected.map(renderServiceConnectedCard)}
                            </CashbackControlServiceListStyled>
                            : <CashbackNoServiceListStyled>
                                {!controlServiceListIsLoading && (controlServiceListIsError
                                    ? <CashbackControlServiceError
                                        translation={tabControlTranslation.statusError}
                                        onClick={loadServiceList}
                                    />
                                    : <CashbackControlServiceNoConnected
                                        translation={tabControlTranslation.statusNoConnectedServices}
                                        onClick={clickOnNoConnectedServicesLink}
                                    />)
                                }
                            </CashbackNoServiceListStyled>
                        }
                    </div>
                </Tab>
                <Tab
                    className="cashback-control__tab tab-available"
                    label={tabControlTranslation.tabAvailable}
                    onClick={() => clickOnInactiveTab(ECashbackService.available, tabControlTranslation.tabAvailable)}
                >
                    <div>
                        <Loader showLoader={controlServiceListIsLoading}/>
                        {serviceList.controlServiceListAvailable.length
                            ? <CashbackControlServiceListStyled>
                                {serviceList.controlServiceListAvailable.map(renderServiceAvailableCard)}
                            </CashbackControlServiceListStyled>
                            : <CashbackNoServiceListStyled>
                                {!controlServiceListIsLoading && (controlServiceListIsError
                                    ? <CashbackControlServiceError
                                        translation={tabControlTranslation.statusError}
                                        onClick={loadServiceList}
                                    />
                                    : <CashbackControlServiceNoAvailable
                                        translation={tabControlTranslation.statusNoAvailableServices}
                                    />)
                                }
                            </CashbackNoServiceListStyled>
                        }
                    </div>
                </Tab>
            </Tabs>

            <div className="cashback-control__tab-control">
                {
                    currentTabType === ECashbackService.connected
                        ? (
                            <Fragment>
                                {!!serviceList.controlServiceListConnected.length && (
                                    <CashbackReserve
                                        currentService={currentConnectedService}
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        tabControlTranslation={tabControlTranslation}
                                        translationCommon={translationCommon}
                                    />
                                )}
                            </Fragment>
                        )
                        : !!serviceList.controlServiceListAvailable.length && (
                        <ArrowTooltip title={getActivationTooltip()} titleAlign="center">
                            <a
                                href={currentAvailableService?.activationLink}
                                rel="noreferrer"
                                target="_blank"
                            >
                                <Button disabled={!currentAvailableService?.activationLink} kind="medium">
                                    {tabControlTranslation.btnActivation}
                                </Button>
                            </a>
                        </ArrowTooltip>
                    )
                }
            </div>
            {
                isCashbackV4Enabled &&
                !!meta?.notificationLoyaltyReward &&
                <CashbackServicesInfo translationCashback={translationCashback}/>
            }
            {
                /* Авторезервирование */
                currentTabType === ECashbackService.connected
                && !controlServiceListIsLoading
                && <CashbackAutoSpendingBonuses
                    isAutoSpendingEnabled={meta.isAutoSpendingEnabled}
                    serviceListEmpty={isServiceListEmpty()}
                    tabControlTranslation={tabControlTranslation}
                />
            }
        </CashbackControlTabContainerStyled>
    );
};

export interface IConnectedProps extends Pick<IPageCashbackReducer,
    'accountSelectCurrent' | 'controlServiceListIsError' | 'controlServiceListIsLoading' | 'meta'> {
    serviceList: TCashbackServiceList;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountSelectCurrent: makeSelectCashbackSelectCurrent(),
    controlServiceListIsError: makeSelectCashbackServiceListIsError(),
    controlServiceListIsLoading: makeSelectCashbackServiceListIsLoading(),
    meta: makeSelectCashbackMeta(),
    serviceList: makeSelectCashbackServiceList(),
});

export interface IDispatchToProps {
    loadServiceList: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchToProps => ({
    loadServiceList: () => dispatch(cashbackControlGetServiceListAction()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CashbackControlServiceTabs);
